import { useEffect, useState } from 'react';

// A deployed version the service worker did not pick up (#973 test, 16/10/2026: an open tab stayed on a bundle from
// days before). Independent of the service worker's events: the page compares the bundle it runs with the one the
// site serves now.

const BUNDLE = /\/assets\/index-[\w-]+\.js/;
const CHECK_MS = 5 * 60 * 1000;

/** The bundle this page runs, from its own script tag (none in development) */
export function loadedBundle(doc: Document = document): string | null {
  const src = Array.from(doc.querySelectorAll<HTMLScriptElement>('script[src]')).map((s) => s.src).find((s) => BUNDLE.test(s));
  return src ? (BUNDLE.exec(src)?.[0] ?? null) : null;
}

/** The bundle a fresh index.html names */
export function servedBundle(html: string): string | null {
  return BUNDLE.exec(html)?.[0] ?? null;
}

/** The newer bundle the site serves when this page runs an older one, checked every 5 minutes and on coming back */
export function useStaleBundle(fetchImpl: typeof fetch = fetch, intervalMs = CHECK_MS): string | null {
  const [newer, setNewer] = useState<string | null>(null);
  useEffect(() => {
    const loaded = loadedBundle();
    if (!loaded) return;
    let cancelled = false;
    const check = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        // A query string the service worker's precache does not match, so the network answers
        const res = await fetchImpl(`/index.html?bundle-check=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const served = servedBundle(await res.text());
        if (!cancelled && served && served !== loaded) setNewer(served);
      } catch {
        // offline: try again later
      }
    };
    void check();
    const id = window.setInterval(() => void check(), intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchImpl, intervalMs]);
  return newer;
}

/**
 * Loads the latest version: the waiting service worker takes over (or the one being installed, once installed), then
 * the page reloads. On its own, tried once per new bundle in a tab, so a version that still does not come never loops;
 * the operator's « Update now » always tries.
 *
 * With no new service worker to take over, a reload goes through the active one, which serves the old page from its
 * precache: « Update now » then did nothing visible (seelab, 31/10/2026, fixed by a forced refresh). Asked, the
 * precache is emptied first, so the page comes from the network. The service worker stays registered, and with it the
 * push subscription.
 */
export async function reloadToLatest(target: string, asked = false): Promise<void> {
  const key = `aidev-reload-for:${target}`;
  try {
    if (sessionStorage.getItem(key) && !asked) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // storage unavailable: still try once in this page's life
  }
  const reg = await navigator.serviceWorker?.getRegistration?.().catch(() => undefined);
  if (reg) {
    await reg.update().catch(() => undefined);
    const next = reg.waiting ?? reg.installing;
    if (next) {
      navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
      if (next.state !== 'installed') {
        await new Promise<void>((resolve) => {
          next.addEventListener('statechange', () => next.state !== 'installing' && resolve());
          window.setTimeout(resolve, 30_000);
        });
      }
      reg.waiting?.postMessage({ type: 'SKIP_WAITING' });
      window.setTimeout(() => window.location.reload(), 10_000);
      return;
    }
  }
  if (asked) {
    const names = await globalThis.caches?.keys().catch(() => [] as string[]) ?? [];
    // Workbox's own precache (`workbox-precache-v2-…`), not the runtime caches; a failed delete reloads as before
    await Promise.all(names.filter((n) => /^workbox-precache-/.test(n)).map((n) => caches.delete(n).catch(() => false)));
  }
  window.location.reload();
}
